// The board under "Ready to merge": a column that holds ready issues widens
// to show their cards, each with its proposal's proof and diff; the others
// fold to narrow rails. Nothing here drags. A person picks ready cards, held
// ones too, and the bar at the foot merges them together in the order picked
// (store.mergeTogether); the cards of a batch that runs or waits stay, and
// say where it is. While the project's policy has a person review
// (Poulpe-688), a proposal awaiting their verdict has its card here too,
// with its diff, Approve and Ask for changes…, and no box to pick it.
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type { Proposal } from '../api/types'
import { StateIcon } from '../components/Icons'
import { useMounted } from '../components/layout'
import { needsServer } from '../components/needsServer'
import { toast } from '../components/toasts'
import { useBoardState, useStore } from '../store/context'
import { stateName } from '../vocab'
import { proposalAnchor, short } from '../queue/words'
import type { ColumnData } from './filters'
import { canMerge, mergeOf, refusalText, togglePick } from './merge'
import { awaitsPerson, isReady, mayConflict, readyProposals } from './ready'
import { ReadyCard } from './ReadyCard'
import { useProposalCopies } from './useProposalCopies'
import './ready.css'

export function ReadyColumns({ columns }: { columns: ColumnData[] }) {
  const { proposals, landings, held, issues, projects, selectedProjectId } = useBoardState()
  const personReviews = projects.get(selectedProjectId ?? '')?.landing_policy?.review === 'person'
  const ready = useMemo(() => readyProposals(selectedProjectId, proposals, landings, personReviews), [selectedProjectId, proposals, landings, personReviews])
  const { hash } = useLocation()
  // Honour a link to one proposal; otherwise show the issue's oldest ready proposal.
  const byIssue = useMemo(() => {
    const found = new Map<string, Proposal>()
    for (const p of ready) if (!found.has(p.issue_id) || hash === `#${proposalAnchor(p.id)}`) found.set(p.issue_id, p)
    return found
  }, [ready, hash])
  const shown = useMemo(() => columns.flatMap((column) => column.items.flatMap((issue) => {
    const proposal = byIssue.get(issue.id)
    return proposal ? [proposal] : []
  })), [columns, byIssue])
  const copies = useProposalCopies(useMemo(() => shown.map((p) => p.id), [shown]))
  const overlaps = useMemo(() => mayConflict(shown, new Map(shown.map((p) => [p.id, copies.get(p.id)?.changes]))), [shown, copies])
  const [picks, setPicks] = useState<string[]>([])
  const board = useRef<HTMLDivElement>(null)
  // A pick no longer shown and ready (it went into a merge, or ended) drops out.
  const picked = useMemo(() => picks.flatMap((id) => {
    const proposal = shown.find((p) => p.id === id)
    return proposal && isReady(proposal, landings) ? [proposal] : []
  }), [picks, shown, landings])
  useEffect(() => {
    if (picks.length !== picked.length) setPicks(picked.map((p) => p.id))
  }, [picks, picked])
  const keyOf = (proposal: Proposal) => issues.get(proposal.issue_id)?.key ?? short(proposal.head)
  return <div className="ready-board" ref={board} tabIndex={-1} role="region" aria-label="Ready to merge">
    <div className="columns ready-columns">{columns.map((column) => {
      const { key, state } = column
      const name = column.column?.name ?? stateName(state)
      if (column.items.length === 0) return <section key={key} className={`column column-${state} ready-rail`} aria-labelledby={`column-title-${key}`} title={`${name}: nothing ready to merge`}>
        <header className="column-header"><StateIcon state={state} /><h2 id={`column-title-${key}`}>{name}</h2><span className="column-count" aria-label="0 issues shown">0</span></header>
      </section>
      return <section key={key} className={`column column-${state} ready-wide`} aria-labelledby={`column-title-${key}`}>
        <header className="column-header">
          <StateIcon state={state} /><h2 id={`column-title-${key}`}>{name}</h2>
          <span className="column-count" aria-label={`${column.items.length} issues shown`}>{column.items.length}</span>
          <span className="ready-column-note">{column.items.some((issue) => { const p = byIssue.get(issue.id); return p !== undefined && awaitsPerson(p) }) ? 'Checked · reviewed or awaiting your review' : 'Checked and reviewed'}</span>
        </header>
        <div className="column-body">{column.items.map((issue) => {
          const proposal = byIssue.get(issue.id)
          if (!proposal) return null
          const merge = mergeOf(proposal.id, landings)
          const pick = picked.indexOf(proposal)
          const awaiting = awaitsPerson(proposal)
          return <ReadyCard key={proposal.id} issue={issue} proposal={proposal} copy={copies.get(proposal.id)} overlaps={overlaps.get(proposal.id) ?? []}
            merge={merge} heldBy={proposal.held_by ? held.get(proposal.held_by) : undefined} picked={picked} pick={pick < 0 ? null : pick + 1}
            awaiting={awaiting} onPick={awaiting ? undefined : () => setPicks(togglePick(picked.map((p) => p.id), proposal.id))} />
        })}</div>
      </section>
    })}</div>
    {picked.length > 0 && <MergeBar picked={picked} keyOf={keyOf} onClear={() => { setPicks([]); board.current?.focus() }} />}
  </div>
}

/** The picks, in order, and Merge together. */
function MergeBar({ picked, keyOf, onClear }: { picked: readonly Proposal[]; keyOf: (proposal: Proposal) => string; onClear: () => void }) {
  const store = useStore()
  const { offline } = useBoardState()
  const mounted = useMounted()
  const [busy, setBusy] = useState(false)
  const n = picked.length
  const can = canMerge(picked)
  const hint = can ? null : n === 1 ? 'Pick one more to merge together.' : 'Ten at most in one merge.'
  const merge = async () => {
    if (busy || !can) return
    setBusy(true)
    const outcome = await store.mergeTogether(picked.map((p) => p.id))
    if (outcome.ok) {
      onClear()
      toast('Sent to merge.')
    } else toast(refusalText(outcome), 'error')
    if (mounted.current) setBusy(false)
  }
  return <div className="merge-bar" role="region" aria-label="Picked to merge">
    <b className="merge-bar-count">{n} selected</b>
    <span className="merge-bar-keys">{picked.map(keyOf).join(' · ')}</span>
    {hint && <span className="merge-bar-hint">{hint}</span>}
    <button type="button" className="button button-quiet merge-bar-clear" onClick={onClear} disabled={busy}>Clear</button>
    {/* Step 4b: Ask the PM agent… */}
    <button type="button" className="button button-primary" onClick={() => void merge()} disabled={busy || offline || !can} {...needsServer(offline)}>
      {n === 1 ? 'Merge alone' : `Merge together (${n})`}
    </button>
  </div>
}
